import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Image } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaUserPlus, FaArrowRight, FaArrowLeft, FaUser, FaPhone, FaMapMarkerAlt, FaIdCard, FaBuilding, FaSchool } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import logo from '../assets/logo.png';
import api from '../services/api';
import './RegisterStep2.css';

const RegisterStep2 = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    location: '',
    university_id: '',
    campus_id: '',
    studentId: '',
    businessName: ''
  });
  const [universities, setUniversities] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch universities on mount
  useEffect(() => {
    api.get('/users/universities/')
      .then(res => {
        if (Array.isArray(res.data.results)) {
          setUniversities(res.data.results);
        } else if (Array.isArray(res.data)) {
          setUniversities(res.data);
        } else {
          setUniversities([]);
        }
      })
      .catch(() => setUniversities([]));
  }, []);

  // Fetch campuses when university changes
  useEffect(() => {
    if (formData.university_id) {
      api.get(`/users/campuses/?university_id=${formData.university_id}`)
        .then(res => {
          if (Array.isArray(res.data.results)) {
            setCampuses(res.data.results);
          } else if (Array.isArray(res.data)) {
            setCampuses(res.data);
          } else {
            setCampuses([]);
          }
        })
        .catch(() => setCampuses([]));
    } else {
      setCampuses([]);
      setFormData(prev => ({ ...prev, campus_id: '' }));
    }
  }, [formData.university_id]);
  
  // Check if we have data from step 1
  useEffect(() => {
    if (!location.state?.email || !location.state?.verified || !location.state?.role) {
      navigate('/register/step1');
    }
  }, [location.state, navigate]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const validateStep = () => {
    const commonFields = ['firstName', 'lastName', 'phoneNumber', 'location', 'university_id', 'campus_id'];
    for (const field of commonFields) {
      if (!formData[field]) {
        if (field === 'university_id') {
          setError('Please select your university');
        } else if (field === 'campus_id') {
          setError('Please select your campus');
        } else {
          setError(`Please fill in your ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`);
        }
        return false;
      }
    }
    const role = location.state.role;
    const sellerType = location.state.sellerType;
    if (role === 'seller') {
      if (sellerType === 'student' && !formData.studentId) {
        setError('Please enter your student ID');
        return false;
      } else if (sellerType === 'professional') {
        if (!formData.businessName) {
          setError('Please enter your business name');
          return false;
        }
      }
    }
    return true;
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateStep()) {
      navigate('/register-step-3', { 
        state: { 
          ...location.state,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phoneNumber: formData.phoneNumber,
          location: formData.location,
          university_id: formData.university_id,
          campus_id: formData.campus_id,
          studentId: formData.studentId,
          businessName: formData.businessName
        } 
      });
    }
  };
  
  const handleBack = () => {
    const backBtn = document.querySelector('.back-btn');
    if (backBtn) {
      backBtn.classList.add('clicked');
      setTimeout(() => {
        navigate('/register-step-1', { state: location.state });
      }, 200);
    } else {
      navigate('/register-step-1', { state: location.state });
    }
  };

  return (
    <Layout>
      <div className="register-step2-page-container">
        <div className="register-step2-split-screen">
          <div className="register-step2-left-panel">
            <div className="register-form-container">
              <img src={logo} alt="UniTrade Logo" className="register-logo" />
              <h2>Personal Information</h2>
              <p className="step-indicator">Step 2 of 3</p>
              <div className="progress-bar-step2">
                <div className="progress-step2" style={{ width: '66.66%' }}></div>
              </div>
              {location.state?.sellerType === 'student' && (
                <Alert variant="info" className="mb-3">
                  You're registering as a Student Seller. After completing all steps, please check your <strong>{location.state.email}</strong> inbox to verify your account.
                </Alert>
              )}
              {error && <Alert variant="danger">{error}</Alert>}
              <form onSubmit={handleSubmit} className="register-step2-form">
                <div className="form-row">
                  <div className="input-group-register">
                    <label htmlFor="firstName">First Name</label>
                    <div className="input-with-icon">
                      <FaUser className="input-icon" />
                      <input 
                        type="text" 
                        id="firstName" 
                        name="firstName" 
                        value={formData.firstName} 
                        onChange={handleChange} 
                        placeholder="John" 
                        required 
                      />
                    </div>
                  </div>
                  <div className="input-group-register">
                    <label htmlFor="lastName">Last Name</label>
                    <div className="input-with-icon">
                      <FaUser className="input-icon" />
                      <input 
                        type="text" 
                        id="lastName" 
                        name="lastName" 
                        value={formData.lastName} 
                        onChange={handleChange} 
                        placeholder="Doe" 
                        required 
                      />
                    </div>
                  </div>
                </div>
                <div className="input-group-register">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <div className="input-with-icon">
                    <FaPhone className="input-icon" />
                    <input 
                      type="tel" 
                      id="phoneNumber" 
                      name="phoneNumber" 
                      value={formData.phoneNumber} 
                      onChange={handleChange} 
                      placeholder="024 123 4567" 
                      required 
                    />
                  </div>
                </div>
                <div className="input-group-register">
                  <label htmlFor="location">Location</label>
                  <div className="input-with-icon">
                    <FaMapMarkerAlt className="input-icon" />
                    <input 
                      type="text" 
                      id="location" 
                      name="location" 
                      value={formData.location} 
                      onChange={handleChange} 
                      placeholder="e.g., Accra, Greater Accra" 
                      required 
                    />
                  </div>
                </div>
                <div className="input-group-register">
                  <label htmlFor="university_id">University/School</label>
                  <div className="input-with-icon">
                    <FaSchool className="input-icon" />
                    <select 
                      id="university_id"
                      name="university_id"
                      value={formData.university_id}
                      onChange={handleChange} 
                      required
                    >
                      <option value="">Select your university</option>
                      {universities.map((uni) => (
                        <option key={uni.id} value={uni.id}>{uni.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="input-group-register">
                  <label htmlFor="campus_id">Campus</label>
                  <div className="input-with-icon">
                    <FaSchool className="input-icon" />
                    <select
                      id="campus_id"
                      name="campus_id"
                      value={formData.campus_id}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select your campus</option>
                      {campuses.map((campus) => (
                        <option key={campus.id} value={campus.id}>{campus.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {location.state.role === 'seller' && (
                  <div className="seller-details-group">
                    <h4>Seller Details</h4>
                    {location.state.sellerType === 'student' ? (
                      <>
                        <div className="input-group-register">
                          <label htmlFor="studentId">Student ID</label>
                          <div className="input-with-icon">
                            <FaIdCard className="input-icon" />
                            <input 
                              type="text" 
                              id="studentId" 
                              name="studentId" 
                              value={formData.studentId} 
                              onChange={handleChange} 
                              placeholder="Enter your student ID" 
                              required 
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="input-group-register">
                        <label htmlFor="businessName">Business Name</label>
                        <div className="input-with-icon">
                          <FaBuilding className="input-icon" />
                          <input 
                            type="text" 
                            id="businessName" 
                            name="businessName" 
                            value={formData.businessName} 
                            onChange={handleChange} 
                            placeholder="Enter your business name" 
                            required 
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="form-navigation">
                  <button type="button" className="back-btn" onClick={handleBack}>
                    <FaArrowLeft /> Back
                  </button>
                  <button type="submit" className="continue-btn">
                    Continue <FaArrowRight />
                  </button>
                </div>
              </form>
            </div>
          </div>
          <div className="register-step2-right-panel">
            <div className="cta-content">
              <FaUserPlus className="cta-icon" />
              <h2>Almost There!</h2>
              <p>Complete your profile to start connecting with the UniTrade community. Your journey is just a few clicks away.</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default RegisterStep2;
